/** Versioned Stair package fixtures for explicit debug URLs. */

import type { CanonicalFixture } from './types';
import { buildCanonicalPackage } from './package';

const STAIR_SCHEMA_VERSION = '1.0.0';
const COMPAT: readonly string[] = Object.freeze([STAIR_SCHEMA_VERSION]);

const wrapperContext = Object.freeze({
    BuildingType: 'SingleFamily',
    PlaceName: 'Dublin, Ireland',
    Latitude: 53.3498,
    Longitude: -6.2603,
    NumberOfLevels: 3,
});

const mm = (value: number) => ({ value, unit: 'mm' });

export const stairFixtures: readonly CanonicalFixture[] = Object.freeze([
    {
        elementType: 'stairs',
        variant: 'compliant',
        schemaVersion: STAIR_SCHEMA_VERSION,
        compatibleSchemaVersions: COMPAT,
        wrapperContext,
        defaultElementType: 'Stair',
        payload: buildCanonicalPackage('stairs', {
            id: 'stair-1',
            type: 'CanonicalStair',
            properties: {
                useClass: 'Private',
                riserHeightMm: mm(190),
                goingMm: mm(250),
                pitchDegrees: 37,
                headroomMm: mm(2100),
                effectiveWidthMm: mm(900),
                handrailHeightMm: mm(950),
                riserCount: 14,
                twoRPlusG: mm(630),
                flights: [
                    {
                        riserCount: 14,
                        risersMm: Array(14).fill(mm(190)),
                        goingsMm: Array(13).fill(mm(250)),
                    },
                ],
                landings: [
                    {
                        lengthMm: mm(1000),
                        effectiveWidthMm: mm(900),
                    },
                ],
            },
        }),
        metadata: {
            description: 'Private stair satisfying TGD Part K riser/going/pitch/headroom targets.',
            expectedRuleIds: [
                'IE-TGD-K-1.1.4',
                'IE-TGD-K-1.1.5',
                'IE-TGD-K-1.1.7',
                'IE-TGD-K-1.1.9',
                'IE-TGD-K-1.1.10',
                'IE-TGD-K-1.1.11',
                'IE-TGD-K-1.1.13',
                'IE-TGD-K-1.1.15',
                'IE-TGD-K-1.1.17',
                'IE-TGD-K-1.1.20',
            ],
        },
    },
    {
        elementType: 'stairs',
        variant: 'noncompliant',
        schemaVersion: STAIR_SCHEMA_VERSION,
        compatibleSchemaVersions: COMPAT,
        wrapperContext,
        defaultElementType: 'Stair',
        payload: buildCanonicalPackage('stairs', {
            id: 'stair-noncompliant-1',
            type: 'CanonicalStair',
            properties: {
                useClass: 'Private',
                riserHeightMm: mm(230),
                goingMm: mm(200),
                pitchDegrees: 48,
                headroomMm: mm(1850),
                effectiveWidthMm: mm(700),
                handrailHeightMm: mm(800),
                riserCount: 14,
                twoRPlusG: mm(660),
                flights: [
                    {
                        riserCount: 14,
                        risersMm: Array(14).fill(mm(230)),
                        goingsMm: Array(13).fill(mm(200)),
                    },
                ],
                landings: [
                    {
                        lengthMm: mm(700),
                        effectiveWidthMm: mm(700),
                    },
                ],
            },
        }),
        metadata: {
            description: 'Private stair violating TGD Part K riser (230>220), going (200<220), pitch (48°>42°) and headroom (<2000).',
        },
    },
    {
        elementType: 'stairs',
        variant: 'missing-input',
        schemaVersion: STAIR_SCHEMA_VERSION,
        compatibleSchemaVersions: COMPAT,
        wrapperContext,
        defaultElementType: 'Stair',
        payload: buildCanonicalPackage('stairs', {
            id: 'stair-missing-1',
            type: 'CanonicalStair',
            properties: {
                useClass: 'Private',
                riserHeightMm: mm(190),
                goingMm: mm(250),
                // pitchDegrees deliberately omitted
                // headroomMm deliberately omitted
                effectiveWidthMm: mm(900),
                handrailHeightMm: mm(950),
                riserCount: 14,
                flights: [],
                landings: [],
            },
        }),
        metadata: {
            description: 'Stair missing pitchDegrees and headroomMm; the live API accepts it and streams cited inconclusive findings for rules that cannot evaluate.',
            expectedVerdict: 'inconclusive',
            expectedEvidence: 'type-error',
            missingInput: true,
        },
    },
    {
        elementType: 'stairs',
        variant: 'invalid',
        schemaVersion: STAIR_SCHEMA_VERSION,
        compatibleSchemaVersions: COMPAT,
        wrapperContext,
        defaultElementType: 'Stair',
        payload: buildCanonicalPackage('stairs', {
            id: 'stair-invalid-1',
            type: 'CanonicalStair',
            properties: {
                useClass: 42 as unknown as string,
                riserHeightMm: -190 as unknown as ReturnType<typeof mm>,
                goingMm: 'about 250mm' as unknown as ReturnType<typeof mm>,
                pitchDegrees: 'steep' as unknown as number,
                headroomMm: null,
                effectiveWidthMm: mm(900),
                handrailHeightMm: mm(950),
                riserCount: -1,
                flights: 'one flight' as unknown as Array<Record<string, unknown>>,
                landings: null,
            },
        }),
        metadata: {
            description: 'Stair payload with wrong types and negative counts; the live API accepts it and streams cited inconclusive type-error findings.',
            expectedVerdict: 'inconclusive',
            expectedEvidence: 'type-error',
            invalid: true,
        },
    },
]);
